/**
 * Landing page copy, transcribed from design/all-copy.txt.
 *
 * Typos in the comp are corrected here and logged in docs/CONTENT-GAPS.md:
 *   "Digital Chack In"        -> "Digital Check In"
 *   "Vehical statues tracking"-> "Vehicle status tracking"
 *   "HOW It Work"             -> "How It Works"
 * The Key Features cards read "20+ courses available" in the comp — leftover
 * copy from an education template. Replaced with the real booking lifecycle
 * from product_server/models/valet_models.py on the dev branch.
 */

export const hero = {
  eyebrow: 'Smart Experience',
  // Split so the gradient runs across all three lines as one fill.
  headline: ['Smart Valet', 'Fast Parking', 'Better Experience'],
  body:
    'Digitize valet operations, parking management, vehicle tracking, staff management, ' +
    'and customer experience through a single platform.',
  primaryCta: { label: 'Book Demo', to: '/contact' },
  secondaryCta: { label: 'Watch Product Tour', to: '/product-tour' },
  stats: {
    live: { label: 'Live Operations', value: '98%' },
    success: { value: '99', unit: '%', label: 'Total valet operations in real-time success' },
    growth: { title: 'Grow Your Company', value: '10.525', delta: '+15.2%', todayLabel: 'Todays', today: '7860 Visitor' },
  },
};

export const about = {
  eyebrow: 'About Us',
  title: 'Smarter Parking Smart Here.',
  body:
    'NimblePark is a valet and parking management platform that helps properties ' +
    'digitize vehicle operations from arrival to retrieval.',
  tags: [
    'Hotels',
    'Malls',
    'Event Venues',
    'Convention Centers',
    'Restaurants',
    'Hospitals',
    'Valet Service Providers',
  ],
};

export const comparison = {
  traditional: {
    title: 'Traditional Parking',
    items: [
      'Paper ticket, easily lost',
      'Vehicle location by memory',
      'Radio calls to find a valet',
      'No visibility for management',
      'Manual retrieval verification',
      'Single-site only',
    ],
  },
  nimblepark: {
    title: 'NimblePark',
    items: [
      'Digital ticket via QR / OTP',
      'Vehicle status tracking',
      'Instant valet assignment',
      'Real-time ops dashboard',
      'OTP-based secure retrieval',
      'Multi-property management',
    ],
  },
};

export const howItWorks = {
  eyebrow: 'How it works',
  title: 'How It Works',
  subtitle: 'From kerbside handover to keys back in hand — every step logged, every vehicle tracked.',
  steps: [
    {
      n: '01',
      kicker: 'Vehicle Check-In',
      title: 'Valet receives vehicle and captures details using the app.',
      image: 'how-01-checkin',
    },
    {
      n: '02',
      kicker: 'Digital Ticket Created',
      title: 'System generates a digital ticket with QR / OTP / Digital ID.',
      image: 'how-02-ticket',
    },
    {
      n: '03',
      kicker: 'Parked and Tracked',
      title: 'The bay is logged against the ticket, so nobody hunts for the car.',
      image: 'how-03-parked',
    },
  ],
  cta: { label: 'View More', to: '/product-tour' },
};

export const keyFeatures = {
  eyebrow: 'Features',
  title: 'Key Features',
  subtitle: 'Everything you need to manage parking operations efficiently and deliver a seamless experience.',
  items: [
    { icon: 'ticket', title: 'Digital Check In', subtitle: 'QR, OTP or digital ID' },
    { icon: 'car', title: 'Vehicle Lifecycle Tracking', subtitle: 'Requested through completed' },
    { icon: 'dashboard', title: 'Real-Time Dashboard', subtitle: 'Live floor and staff view' },
    { icon: 'user-check', title: 'Valet Assignment', subtitle: 'Auto-assign the closest valet' },
    { icon: 'shield-key', title: 'OTP-Based Retrieval', subtitle: 'Verified handover every time' },
    { icon: 'buildings', title: 'Multi-Property Management', subtitle: 'One login, every site' },
  ],
  cta: { label: 'View all', to: '/product-tour' },
};

export const whyNimblepark = {
  eyebrow: 'Features',
  title: 'Why Nimblepark',
  subtitle:
    'The all-in-one digital platform for smarter valet parking, vehicle tracking, ' +
    'staff management, and seamless guest experiences.',
  items: [
    { title: 'Reduce Vehicle Retrieval Time', image: 'why-01-retrieval' },
    { title: 'Improve Customer Experience', image: 'why-02-experience' },
    { title: 'Eliminate Paper Tickets', image: 'why-03-paperless' },
  ],
  cta: { label: 'View all', to: '/benefits-calculator' },
};

export const testimonials = {
  eyebrow: 'Our Testimonials',
  title: 'Trusted By Leaders',
  subtitle: 'Leading brands trust us to deliver impact—and they say it best',
  // Seven testimonials, transcribed verbatim from the comp. Only the product
  // name is changed ("Property Valet" -> "NimblePark"); see docs/CONTENT-GAPS.md.
  items: [
    {
      quote: 'Innovative, reliable, and goal-oriented. Their strategies gave our brand a fresh identity across channels.',
      name: 'Sophia Green',
      role: 'CEO',
      rating: 4.5,
      avatar: 'avatar-sophia',
    },
    {
      quote: 'NimblePark has made our operations faster and more organized. Highly recommended!',
      name: 'Daniel Carter',
      role: 'Product Manager',
      rating: 4.5,
      avatar: 'avatar-daniel',
    },
    {
      quote: 'Real-time tracking and digital check-ins have transformed our customer experience.',
      name: 'Emily Ross',
      role: 'Marketing Head',
      rating: 4.5,
      avatar: 'avatar-emily',
    },
    {
      quote: 'Creative insights and data-backed results. Their campaigns made our brand presence more stronger.',
      name: 'Liam Mitchel',
      role: 'CEO',
      rating: 4.5,
      avatar: 'avatar-liam',
    },
    {
      quote: 'Every stage was handled with care and precision. More than a service provider—they’re partners in growth.',
      name: 'Olivia Hayes',
      role: 'Product Manager',
      rating: 4.5,
      avatar: 'avatar-generic',
    },
    {
      quote: 'Real-time tracking and digital check-ins have transformed our customer experience.',
      name: 'Ethan Brooks',
      role: 'CEO',
      rating: 4.5,
      avatar: 'avatar-ethan',
    },
    {
      quote: 'The platform is simple to use and has greatly improved our valet service efficiency.',
      name: 'Mateo Alvarez',
      role: 'Marketing Head',
      rating: 4.5,
      avatar: 'avatar-mateo',
    },
  ],
  cta: { label: 'View all', to: '/#testimonials' },
};

export const articles = {
  eyebrow: 'Our Blogs',
  title: 'Read Articles',
  subtitle:
    'Discover the latest trends, ideas, and practical insights shaping the future ' +
    'of smart parking and vehicle management.',
  featured: {
    title: 'Why Paperless Parking Is the Future',
    excerpt:
      'See how digital valet tickets can eliminate paperwork, reduce operational costs, ' +
      'and create a faster parking experience.',
    age: '30 mins ago',
    image: 'article-01-paperless',
    to: '/#articles',
  },
  items: [
    {
      title: 'Faster, Smarter & More Seamless Parking',
      excerpt:
        'QR-free digital retrieval helps teams locate and return vehicles faster, ' +
        'creating a seamless and paperless customer experience.',
      age: '40 mins ago',
      image: 'article-02-seamless',
      to: '/#articles',
    },
    {
      title: 'Reduce Vehicle Retrieval Time',
      excerpt: 'Get vehicles back to customers faster with digital tracking.',
      age: '40 mins ago',
      image: 'article-03-retrieval',
      to: '/#articles',
    },
  ],
  cta: { label: 'Read More', to: '/#articles' },
};

/**
 * Destinations are placeholders per the build decision — the product apps live
 * on the dev branch and have no public URLs yet. Tracked in docs/CONTENT-GAPS.md.
 */
export const associatePortal = {
  eyebrow: 'Associate Portal',
  title: 'Already using NimblePark?',
  subtitle: 'Continue managing your valet operations smarter with NimblePark’s powerful digital tools.',
  links: [
    { label: 'Associate Login', href: '#' },
    { label: 'Customer Login', href: '#' },
    { label: 'Property Login', href: '#' },
    { label: 'Event Login', href: '#' },
    { label: 'VSP Login', href: '#' },
    { label: 'Android App', href: '#' },
    { label: 'IOS App', href: '#' },
    { label: 'Web Portal', href: '#' },
  ],
};

export const requestDemo = {
  eyebrow: 'Demo',
  title: 'Request a Demo',
  placeholder: 'Enter your email',
  cta: 'Get Started',
  image: 'demo-valet-handover',
};
